import { useCallback } from "react";
// Common Components
import { CommonTableUI } from "src/Tables/TableTemplate/common";
import CellWithChart from "src/Tables/TableTemplate/CellWithChart";
import TableTemplate, {
  IExtraTypeUIProps,
  IFilterTableCols,
} from "src/Tables/TableTemplate/tableTemplate";
// Local Components
import { Additional } from "./Additional";
import ChartSearchParamUI from "./ChartSearchParamUI";
// Hooks
import { useScroll } from "src/Hooks/useScroll";
import { usePairWatchListSnapshot } from "src/Hooks/usePairWatchListSnapshot";
// Types
import { ExtraType, Report } from "src/Interfaces/TableInterfaces";
import {
  IChartCommonSearchParam,
  IRolePairPositionSearchParam,
  IRolePairPositionTableReport,
} from "src/Interfaces/deltaHedgeAccounts";
// Requests
import { getRolePairPositionTable } from "src/Requests/deltaHedgeAccounts";
import { getOptionDomainChart } from "src/Requests/optionDomain";

interface IProps {
  filterCols: IFilterTableCols;
  searchParams: IRolePairPositionSearchParam;
  refreshFlag?: boolean;
  onRowClick: (reports: IRolePairPositionTableReport) => void;
  selectedTableRow: number | null;
}

const RolePairPositionTable = ({
  filterCols,
  searchParams,
  refreshFlag,
  onRowClick,
  selectedTableRow,
}: IProps) => {
  const { ref } = useScroll<HTMLDivElement>("pairPositionTableScroll");
  const { snapshotPending, retryKey, handleSnapshotError, handleSnapshotResponse } =
    usePairWatchListSnapshot();

  const getExtraTypeUI = useCallback(
    ({
      headerName,
      extraType,
      row,
      cellType,
      translateHeaderName,
    }: IExtraTypeUIProps<IRolePairPositionTableReport>) => {
      switch (extraType) {
        case ExtraType.WithChart:
          return (
            <CellWithChart<
              IRolePairPositionTableReport,
              IChartCommonSearchParam
            >
              SearchParamUI={({ changeHandler, onSubmit, searchParams }) => (
                <ChartSearchParamUI
                  searchParams={searchParams}
                  onSubmit={onSubmit}
                  onChange={changeHandler}
                />
              )}
              cellType="Price"
              headerName={headerName}
              title={[translateHeaderName || headerName, row.instrumentName1]
                .filter(Boolean)
                .join(" — ")}
              row={row}
              extraType={extraType}
              chartGetter={(searchParams) =>
                getOptionDomainChart(
                  headerName,
                  row.instrumentId1,
                  row.instrumentName1,
                  row.instrumentId2,
                  row.instrumentName2,
                  row.positionType1,
                  searchParams.fromDate,
                  searchParams.toDate,
                )
              }
              withDate
            />
          );

        default:
          return <></>;
      }
    },
    [searchParams],
  );

  return (
    <>
      {snapshotPending && (
        <div className="py-6 text-center text-sm text-gray-600">
          اطلاعات جفت پوزیشن در حال آماده‌سازی است…
        </div>
      )}
      <TableTemplate<IRolePairPositionSearchParam, IRolePairPositionTableReport>
        TableUI={({
          reports,
          onAction,
          sortingType,
          setSortingType,
          scrollKey,
        }) => (
          <CommonTableUI
            reports={reports}
            onAction={onAction}
            filterCols={filterCols}
            tableBoxClassName="!max-h-[600px] !max-w-full"
            AdditionalBody={Additional}
            sortingType={sortingType}
            setSortingType={setSortingType}
            refreshFlag={refreshFlag}
            onTableRowClick={onRowClick}
            activeRowId={selectedTableRow || undefined}
            getExtraTypeUI={getExtraTypeUI}
            ref={ref}
            scrollKey={scrollKey}
          />
        )}
        refreshTableTime={5000}
        getDataTable={getRolePairPositionTable}
        searchParameters={searchParams}
        withSort
        hideUntilResponse
        silentRefreshKey={retryKey}
        onFetchError={handleSnapshotError}
        onResponse={handleSnapshotResponse}
      />
    </>
  );
};

export default RolePairPositionTable;
